import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';
import { VhIconComponent } from '../icon/icon.component';

export type VhRatingSize = 'sm' | 'md';

/**
 * Read-only star rating display. Fills `value` / 5 stars in `currentColor`
 * and optionally shows the count beside it. Half-stars supported.
 */
@Component({
  selector: 'vh-rating',
  standalone: true,
  imports: [VhIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="vh-rating__stars" aria-hidden="true">
      @for (star of stars(); track $index) {
        <span class="vh-rating__star" [class.vh-rating__star--filled]="star === 'full'">
          <vh-icon name="star" [size]="iconSize()" [strokeWidth]="1.5" />
          @if (star === 'half') {
            <span class="vh-rating__half">
              <vh-icon name="star" [size]="iconSize()" [strokeWidth]="1.5" />
            </span>
          }
        </span>
      }
    </span>
    @if (showValue()) {
      <span class="vh-rating__value">{{ valueLabel() }}</span>
    }
    @if (showCount() && count() !== null && count() !== undefined) {
      <span class="vh-rating__count">({{ count() }})</span>
    }
    <span class="vh-rating__sr">Rated {{ valueLabel() }} out of 5</span>
  `,
  styleUrl: './rating.component.scss',
  host: {
    '[class.vh-rating]': 'true',
    '[class.vh-rating--sm]': 'size() === "sm"',
    '[attr.role]': '"img"',
    '[attr.aria-label]': 'ariaLabel()',
  },
})
export class VhRatingComponent {
  readonly value = input.required<number>();
  readonly count = input<number | null>(null);
  readonly size = input<VhRatingSize>('md');
  readonly showCount = input(true, { transform: booleanAttribute });
  readonly showValue = input(false, { transform: booleanAttribute });

  readonly iconSize = computed<'sm' | 'md'>(() => (this.size() === 'sm' ? 'sm' : 'md'));
  readonly valueLabel = computed(() => this.value().toFixed(1));
  readonly ariaLabel = computed(() => {
    const c = this.count();
    const base = `${this.valueLabel()} out of 5`;
    return c != null ? `${base}, ${c} reviews` : base;
  });

  readonly stars = computed<Array<'full' | 'half' | 'empty'>>(() => {
    const v = Math.max(0, Math.min(5, this.value()));
    const out: Array<'full' | 'half' | 'empty'> = [];
    for (let i = 0; i < 5; i++) {
      const diff = v - i;
      if (diff >= 0.75) out.push('full');
      else if (diff >= 0.25) out.push('half');
      else out.push('empty');
    }
    return out;
  });
}
